import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import logoMark from '@assets/logo_mark.svg'
import { userTitle } from '@helpers/formatters'
import { useAuthSession, useAuthStore } from '@stores/authStore'
import type { MenuItem } from '@app-types/api/auth'
import { redirectToMainLogin } from '@routing/subdomains'
import { isSpaRoute } from '@routing/spaRoutes'
import GameSwitcherPanel from '@components/main/GameSwitcherPanel'
import PasswordChangeModal from '@components/main/PasswordChangeModal'
import ThemeModeToggle from '@components/main/ThemeModeToggle'

function navLinkClassName(isActive: boolean) {
  return `rounded-md px-3 py-2 text-sm font-semibold no-underline transition ${
    isActive
      ? 'bg-slate-100 text-slate-950 dark:bg-zinc-800 dark:text-white'
      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-zinc-800 dark:hover:text-white'
  }`
}

function exportMenuItem(items: MenuItem[]) {
  return items.find((item) => item.title === 'Экспорт' && item.url)
}

function menuItems(items: MenuItem[]) {
  return items.filter((item) => !item.isHeader && item.url)
}

function isActiveUrl(pathname: string, url: string) {
  const normalizedUrl = url.endsWith('/') ? url.slice(0, -1) : url
  const normalizedPathname = pathname.replace(/\/$/, '') || '/'

  return (
    normalizedPathname === normalizedUrl ||
    (normalizedUrl !== '/' && normalizedPathname.startsWith(`${normalizedUrl}/`))
  )
}

function isSidebarPathActive(pathname: string, items: MenuItem[]) {
  return menuItems(items).some((item) => isActiveUrl(pathname, item.url))
}

function isGameTopItemActive(
  pathname: string,
  item: MenuItem,
  sidebarItems: MenuItem[],
) {
  if (item.title === 'Summary') {
    return isSidebarPathActive(pathname, sidebarItems)
  }

  return isActiveUrl(pathname, item.url)
}

function MainHeader() {
  const location = useLocation()
  const session = useAuthSession()
  const logoutSession = useAuthStore((state) => state.logoutSession)
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [isLogoutInProgress, setIsLogoutInProgress] = useState(false)
  const currentApp = session?.currentApp
  const canSwitchGame = Boolean(currentApp && session?.apps.length)
  const isSwitcherVisible = canSwitchGame && isSwitcherOpen
  const isUserMenuVisible = Boolean(session?.user && isUserMenuOpen)
  const showMainMenu = !currentApp
  const exportItem = exportMenuItem(session?.menu.topItems ?? [])
  const gameTopItems = currentApp ? menuItems(session?.menu.topItems ?? []) : []
  const currentUserTitle = session ? userTitle(session) : ''

  function handleLogout() {
    if (!session || isLogoutInProgress) {
      return
    }

    setIsLogoutInProgress(true)
    void logoutSession(session.csrfToken)
      .then(() => {
        redirectToMainLogin()
      })
      .catch(() => {
        setIsLogoutInProgress(false)
      })
  }

  function closeFloatingMenus() {
    setIsSwitcherOpen(false)
    setIsUserMenuOpen(false)
  }

  return (
    <>
      {isSwitcherVisible || isUserMenuVisible ? (
        <button
          aria-label="Закрыть меню"
          className="fixed inset-0 z-40 cursor-default"
          onClick={closeFloatingMenus}
          type="button"
        />
      ) : null}

      <header
        className="relative z-50 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-5 dark:border-zinc-800 dark:bg-zinc-900"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            closeFloatingMenus()
          }
        }}
      >
        <div className="flex min-w-0 items-center gap-4">
          <button
            aria-expanded={canSwitchGame ? isSwitcherOpen : undefined}
            className={`flex min-w-0 items-center gap-3 rounded-md px-1 py-1 text-left transition ${
              canSwitchGame
                ? 'cursor-pointer hover:bg-slate-100 dark:hover:bg-zinc-800'
                : 'cursor-default'
            }`}
            disabled={!canSwitchGame}
            onClick={() => {
              setIsUserMenuOpen(false)
              setIsSwitcherOpen((value) => !value)
            }}
            type="button"
          >
            <img
              className="size-9 rounded-lg"
              src={logoMark}
              width="36"
              height="36"
              alt=""
              aria-hidden="true"
            />
            <div>
              <p className="text-sm font-semibold leading-tight">
                {currentApp?.name || 'GRS'}
              </p>
              <p className="text-xs leading-tight text-slate-500 dark:text-slate-400">
                {currentApp ? currentApp.slug : 'Games Revenue Statistics'}
              </p>
            </div>
            {canSwitchGame ? (
              <span
                aria-hidden="true"
                className={`ml-1 size-2.5 shrink-0 border-r-2 border-b-2 border-slate-500 transition-transform dark:border-slate-300 ${
                  isSwitcherVisible ? '-rotate-135' : 'rotate-45'
                }`}
              />
            ) : null}
          </button>

          {showMainMenu ? (
            <nav className="flex items-center gap-1">
              <Link
                className={navLinkClassName(location.pathname === '/')}
                to="/"
              >
                Игры
              </Link>
              {exportItem ? (
                <Link
                  className={navLinkClassName(
                    isActiveUrl(location.pathname, exportItem.url),
                  )}
                  to={exportItem.url}
                >
                  {exportItem.title}
                </Link>
              ) : null}
            </nav>
          ) : null}

          {currentApp && gameTopItems.length > 0 ? (
            <nav className="flex items-center gap-1 overflow-x-auto">
              {gameTopItems.map((item) => {
                const isActive = isGameTopItemActive(
                  location.pathname,
                  item,
                  session?.menu.sidebarItems ?? [],
                )

                if (isSpaRoute(item.url)) {
                  return (
                    <Link
                      className={navLinkClassName(isActive)}
                      key={`${item.title}-${item.url}`}
                      to={item.url}
                    >
                      {item.title}
                    </Link>
                  )
                }

                return (
                  <a
                    className={navLinkClassName(isActive)}
                    href={item.url}
                    key={`${item.title}-${item.url}`}
                  >
                    {item.title}
                  </a>
                )
              })}
            </nav>
          ) : null}
        </div>

        <nav className="flex items-center gap-3 text-sm">
          <ThemeModeToggle />

          {session?.user ? (
            <div className="relative">
              <button
                aria-expanded={isUserMenuVisible}
                className="flex max-w-72 items-center gap-2 rounded-md border border-slate-200 px-3 py-2 font-semibold text-slate-700 transition hover:border-violet-400 dark:border-zinc-700 dark:text-slate-100 dark:hover:border-violet-400"
                onClick={() => {
                  setIsSwitcherOpen(false)
                  setIsUserMenuOpen((value) => !value)
                }}
                type="button"
              >
                <span className="truncate">{currentUserTitle}</span>
                <span
                  aria-hidden="true"
                  className={`size-2 shrink-0 border-r-2 border-b-2 border-slate-500 transition-transform dark:border-slate-300 ${
                    isUserMenuVisible ? '-rotate-135' : 'rotate-45'
                  }`}
                />
              </button>

              {isUserMenuVisible ? (
                <div className="absolute right-0 top-full z-[60] mt-2 w-56 rounded-md border border-slate-200 bg-white py-2 shadow-lg dark:border-zinc-800 dark:bg-zinc-900">
                  <button
                    className="block w-full px-4 py-2 text-left text-sm font-semibold text-slate-700 no-underline hover:bg-slate-100 hover:text-slate-950 dark:text-slate-200 dark:hover:bg-zinc-800 dark:hover:text-white"
                    onClick={() => {
                      setIsUserMenuOpen(false)
                      setIsPasswordModalOpen(true)
                    }}
                    type="button"
                  >
                    Смена пароля
                  </button>
                  <button
                    className="block w-full px-4 py-2 text-left text-sm font-semibold text-slate-700 no-underline hover:bg-slate-100 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-60 dark:text-slate-200 dark:hover:bg-zinc-800 dark:hover:text-white"
                    disabled={isLogoutInProgress}
                    onClick={handleLogout}
                    type="button"
                  >
                    {isLogoutInProgress ? 'Выход...' : 'Выход'}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </nav>

        {isSwitcherVisible ? (
          <GameSwitcherPanel onClose={() => setIsSwitcherOpen(false)} />
        ) : null}

        {isPasswordModalOpen ? (
          <PasswordChangeModal
            onClose={() => setIsPasswordModalOpen(false)}
            open={isPasswordModalOpen}
          />
        ) : null}
      </header>
    </>
  )
}

export default MainHeader
